import { useEffect, useRef, useState } from "react";
import type { Arc, Globe, Marker } from "cobe";
type LogisticsGlobeProps = {
  ariaLabel: string;
  onReady?: () => void;
};

const LOCATIONS = {
  shenzhen: [22.5431, 114.0579],
  taiwan: [25.033, 121.5654],
  kyiv: [50.4501, 30.5234],
  bratislava: [48.1486, 17.1077],
  saoPaulo: [-23.5505, -46.6333],
  almaty: [43.2389, 76.8897],
  calgary: [51.0447, -114.0719],
  london: [51.5072, -0.1276],
  athens: [37.9838, 23.7275],
  warsaw: [52.2297, 21.0122],
  budapest: [47.4979, 19.0402],
  tallinn: [59.437, 24.7536],
} satisfies Record<string, [number, number]>;

const HUBS: Marker[] = [
  { location: LOCATIONS.shenzhen, size: 0.095, color: [0.03, 0.38, 0.86] },
  { location: LOCATIONS.taiwan, size: 0.075, color: [0.25, 0.65, 1] },
];

const DESTINATIONS: Marker[] = [
  { location: LOCATIONS.kyiv, size: 0.04 },
  { location: LOCATIONS.bratislava, size: 0.055, color: [0.1, 0.48, 0.92] },
  { location: LOCATIONS.saoPaulo, size: 0.038 },
  { location: LOCATIONS.almaty, size: 0.038 },
  { location: LOCATIONS.calgary, size: 0.038 },
  { location: LOCATIONS.london, size: 0.03 },
  { location: LOCATIONS.athens, size: 0.03 },
  { location: LOCATIONS.warsaw, size: 0.03 },
  { location: LOCATIONS.budapest, size: 0.03 },
  { location: LOCATIONS.tallinn, size: 0.03 },
];

const LONG_HAUL_COLOR: [number, number, number] = [0.05, 0.4, 0.9];
const REGIONAL_COLOR: [number, number, number] = [0.25, 0.66, 1];

const ARCS: Arc[] = [
  { from: LOCATIONS.shenzhen, to: LOCATIONS.kyiv, color: LONG_HAUL_COLOR },
  { from: LOCATIONS.taiwan, to: LOCATIONS.shenzhen, color: LONG_HAUL_COLOR },
  { from: LOCATIONS.shenzhen, to: LOCATIONS.bratislava, color: LONG_HAUL_COLOR },
  { from: LOCATIONS.bratislava, to: LOCATIONS.saoPaulo, color: LONG_HAUL_COLOR },
  { from: LOCATIONS.shenzhen, to: LOCATIONS.almaty, color: LONG_HAUL_COLOR },
  { from: LOCATIONS.shenzhen, to: LOCATIONS.calgary, color: LONG_HAUL_COLOR },
  { from: LOCATIONS.bratislava, to: LOCATIONS.london, color: REGIONAL_COLOR },
  { from: LOCATIONS.bratislava, to: LOCATIONS.athens, color: REGIONAL_COLOR },
  { from: LOCATIONS.bratislava, to: LOCATIONS.warsaw, color: REGIONAL_COLOR },
  { from: LOCATIONS.bratislava, to: LOCATIONS.budapest, color: REGIONAL_COLOR },
  { from: LOCATIONS.bratislava, to: LOCATIONS.tallinn, color: REGIONAL_COLOR },
];

const INITIAL_PHI = 3.0;
const AUTO_ROTATION_SPEED = 0.00014;
const INERTIA_DECAY_PER_FRAME = 0.94;
const MAX_INERTIA_SPEED = 0.012;

export function LogisticsGlobe({ ariaLabel, onReady }: LogisticsGlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rotationRef = useRef(INITIAL_PHI);
  const dragStartRef = useRef<number | null>(null);
  const dragOffsetRef = useRef(0);
  const pointerIdRef = useRef<number | null>(null);
  const inertiaVelocityRef = useRef(0);
  const lastDragXRef = useRef<number | null>(null);
  const lastDragTimeRef = useRef<number | null>(null);
  const readyNotifiedRef = useRef(false);
  const [fallback, setFallback] = useState(false);

  const markReady = () => {
    if (readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    onReady?.();
  };

  const finishDrag = () => {
    if (dragStartRef.current === null) return;
    rotationRef.current += dragOffsetRef.current;
    dragStartRef.current = null;
    dragOffsetRef.current = 0;
    lastDragXRef.current = null;
    lastDragTimeRef.current = null;

    const canvas = canvasRef.current;
    const pointerId = pointerIdRef.current;
    if (canvas && pointerId !== null && canvas.hasPointerCapture(pointerId)) {
      canvas.releasePointerCapture(pointerId);
    }
    pointerIdRef.current = null;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let globe: Globe | null = null;
    let frame = 0;
    let disposed = false;
    let visible = true;
    let lastFrame = performance.now();
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      if (!globe) return;
      const size = Math.max(1, canvas.offsetWidth);
      globe.update({ width: size * pixelRatio, height: size * pixelRatio });
    };

    const resizeObserver = new ResizeObserver(resize);
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
      },
      { rootMargin: "120px" },
    );
    const handleWindowBlur = () => finishDrag();
    const handleWindowPointerUp = () => finishDrag();
    const handleLostPointerCapture = () => finishDrag();

    const start = async () => {
      try {
        const { default: createGlobe } = await import("cobe");
        if (disposed) return;

        const size = Math.max(1, canvas.offsetWidth);
        globe = createGlobe(canvas, {
          width: size * pixelRatio,
          height: size * pixelRatio,
          devicePixelRatio: pixelRatio,
          phi: rotationRef.current,
          theta: 0.2,
          dark: 0,
          diffuse: 1.15,
          mapSamples: 20_000,
          mapBrightness: 5.5,
          mapBaseBrightness: 0.02,
          baseColor: [0.86, 0.93, 1],
          markerColor: [0.08, 0.43, 0.9],
          glowColor: [0.72, 0.86, 1],
          opacity: 0.92,
          scale: 0.94,
          offset: [0, 0],
          markers: [...HUBS, ...DESTINATIONS],
          markerElevation: 0.035,
          arcs: ARCS,
          arcColor: [0.08, 0.43, 0.9],
          arcWidth: 0.65,
          arcHeight: 0.17,
        });

        resizeObserver.observe(canvas);
        visibilityObserver.observe(canvas);
        canvas.classList.add("is-ready");
        markReady();

        const render = (now: number) => {
          const elapsed = Math.min(now - lastFrame, 32);
          lastFrame = now;

          if (!reduceMotion && visible && dragStartRef.current === null) {
            rotationRef.current += elapsed * AUTO_ROTATION_SPEED;
            if (Math.abs(inertiaVelocityRef.current) > 0.00001) {
              rotationRef.current += inertiaVelocityRef.current * elapsed;
              inertiaVelocityRef.current *= Math.pow(INERTIA_DECAY_PER_FRAME, elapsed / 16);
            } else {
              inertiaVelocityRef.current = 0;
            }
          }

          globe?.update({ phi: rotationRef.current + dragOffsetRef.current });
          frame = requestAnimationFrame(render);
        };

        frame = requestAnimationFrame(render);
      } catch {
        if (!disposed) {
          setFallback(true);
          markReady();
        }
      }
    };

    void start();
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("pointerup", handleWindowPointerUp);
    canvas.addEventListener("lostpointercapture", handleLostPointerCapture);

    return () => {
      finishDrag();
      disposed = true;
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("pointerup", handleWindowPointerUp);
      canvas.removeEventListener("lostpointercapture", handleLostPointerCapture);
      globe?.destroy();
    };
  }, []);

  return (
    <figure className="relative mx-auto w-full">
      <div aria-hidden className="logi-cobe-aura absolute inset-[12%] rounded-full" />
      <div aria-hidden className="logi-cobe-orbit logi-cobe-orbit-a" />
      <div aria-hidden className="logi-cobe-orbit logi-cobe-orbit-b" />
      {fallback ? (
        <div role="img" aria-label={ariaLabel} className="logi-cobe-fallback">
          <span />
          <span />
          <span />
        </div>
      ) : (
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={ariaLabel}
          className="logi-cobe-canvas relative z-10 aspect-square w-full cursor-grab active:cursor-grabbing"
          onPointerDown={(event) => {
            dragStartRef.current = event.clientX;
            pointerIdRef.current = event.pointerId;
            inertiaVelocityRef.current = 0;
            lastDragXRef.current = event.clientX;
            lastDragTimeRef.current = event.timeStamp;
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (dragStartRef.current === null) return;
            dragOffsetRef.current = (event.clientX - dragStartRef.current) / 150;
            if (lastDragXRef.current !== null && lastDragTimeRef.current !== null) {
              const deltaX = event.clientX - lastDragXRef.current;
              const deltaTime = Math.max(event.timeStamp - lastDragTimeRef.current, 1);
              const velocity = (deltaX / 150) / deltaTime;
              inertiaVelocityRef.current = Math.max(
                -MAX_INERTIA_SPEED,
                Math.min(MAX_INERTIA_SPEED, velocity),
              );
            }
            lastDragXRef.current = event.clientX;
            lastDragTimeRef.current = event.timeStamp;
          }}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
        />
      )}

    </figure>
  );
}
